import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Paper,
  Grid,
  Radio,
  FormControlLabel,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ModeStandbyIcon from "@mui/icons-material/ModeStandby";
import CommentIcon from "@mui/icons-material/Comment";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { LoaderWithoutHeight } from "components/Loader";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { themes, infoStyle, defaultAllSelection } from "services/constants";
import { useFetch } from "services/hooks/useFetch";
import EditIcon from "@mui/icons-material/Edit";

// Timeline Helpers (Shared with AdvanceSalaryPanel)
export const buildStatusHistory = (data) => {
  if (!data) return [];
  const history = [];

  if (data.applied_at || data.request_date) {
    history.push({
      status: "Applied",
      user: data.applied_by || data.user_name || "User",
      date: data.applied_at || data.request_date,
      color: "#00ab66",
    });
  }

  if (data.status === "Rejected") {
    history.push({
      status: "Pre-Approval: Rejected",
      user: data.rejected_by || data.approver_name || "Admin",
      date: data.rejected_at || "N/A",
      color: "#ff0000",
    });
    if (data.rejection_reason) {
      history.push({
        status: "Reason",
        reason: data.rejection_reason,
        isReason: true,
        color: "#d3d3d3",
      });
    }
  }

  if (data.status === "Approved" || data.status === "Active") {
    history.push({
      status: data.status === "Active" ? "Activated" : "Approved",
      user: data.approved_by || data.approver_name || "Admin",
      date: data.approved_at || "N/A",
      color: "#00ab66",
    });
  }

  return history;
};

// Fixed Custom Timeline Component (No double dots, clean start/stop connector)
export const CustomTimeline = ({ history }) => (
  <Box sx={{ mt: 1, ml: 1 }}>
    {history.map((item, index) => {
      const isLast = index === history.length - 1;
      return (
        <Box
          key={index}
          sx={{ position: "relative", pl: 3, pb: isLast ? 0 : 3 }}
        >
          {/* Connector Line (Only render if it's NOT the last item) */}
          {!isLast && (
            <Box
              sx={{
                position: "absolute",
                left: "4px", // Centers exactly under the 10px dot
                top: "14px",
                bottom: "-2px",
                width: "2px",
                bgcolor: "#eee",
              }}
            />
          )}
          {/* Timeline Dot Badge */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 5,
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: item.color || "#ccc",
              zIndex: 1,
            }}
          />
          {item.isReason ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <strong>{item.status}:</strong> {item.reason}
            </Typography>
          ) : (
            <Typography variant="body2">
              <strong>{item.status}</strong>{" "}
              {item.user ? `by ${item.user}` : ""} On {item.date}
            </Typography>
          )}
        </Box>
      );
    })}
  </Box>
);

const InfoCard = ({ label, value, icon: Icon, color }) => (
  <Box
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      bgcolor: color,
      border: "1px solid #eee",
    }}
  >
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mb: 1, opacity: 0.7 }}
    >
      {Icon && <Icon sx={{ fontSize: 18 }} />}
      <Typography variant="caption" fontWeight="bold">
        {label}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

const SummaryCard = ({ title, amount, icon: Icon, color, lightColor }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, borderRadius: 2, borderColor: color, flex: 1 }}
  >
    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
      <Box
        sx={{
          p: 0.5,
          borderRadius: 1,
          bgcolor: lightColor,
          color: color,
          display: "flex",
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Typography variant="caption" color="text.secondary" fontWeight="bold">
        {title}
      </Typography>
    </Stack>
    <Typography variant="h5" fontWeight="bold">
      {amount}
    </Typography>
  </Paper>
);

const TeamAdvanceSalaryPanel = ({
  onClose,
  reCallApi,
  mode = "create",
  initialData = null,
  onAction,
}) => {
  const { domain, userDetails, token } = useSelector(
    (state) => state.tokenReducer,
  );

  const isView = mode === "view";
  const isCreate = mode === "create";
  const isAction = mode === "action"; // Manager Approval Mode
  const isDetailedView =
    isView &&
    (initialData?.status === "Approved" || initialData?.status === "Active");

  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Department & Employee Selection State
  const [departmentData, setDepartmentData] = useState([]);
  const [departmentName, setDepartmentName] = useState("All Department");
  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const [selectedUser, setSelectedUser] = useState(null);

  const [userId, setUserId] = useState(null);
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);

  const { result: membersData, setLoad: reCallMemberApi } = useFetch(
    `${domain}get-department-based-user/?department_id=${departmentId}&user_id=${userDetails.id}`,
  );

  // State for Create Mode
  const [createData, setCreateData] = useState({
    amount: "",
    reason: "",
    repayment_mode: "Full",
    repayment_date: dayjs()
      .add(1, "month")
      .startOf("month")
      .format("YYYY-MM-DD"),
    no_of_installments: 1,
  });

  // State for Action Mode (Approve/Reject)
  const [actionData, setActionData] = useState({
    approved_amount: "",
    repayment_mode: "Full",
    approved_installments: 1,
    approved_repayment_date: "",
    approver_comments: "",
    rejection_reason: "",
  });

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartmentData = async () => {
      try {
        const response = await fetch(
          `${domain}get-department/?user_id=${userDetails.id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setDepartmentData(res);
        }
      } catch (error) {
        showToast("error", "Failed to load departments");
      }
    };
    if (isCreate) {
      fetchDepartmentData();
    }
  }, [domain, userDetails.id, token, isCreate]);

  // Handle Department Selection
  const updateDepartmentName = (event, data) => {
    if (data) {
      setDepartmentName(data.department_name || data.label);
      setDepartmentId(data.id);

      setUserId(null);
      setSelectedUser(null);
      reCallMemberApi(true);
    } else {
      // Reset to "All Departments" when cleared
      setDepartmentName("All Department");
      setDepartmentId(defaultAllSelection);

      setUserId(null);
      setSelectedUser(null);
      reCallMemberApi(true);
    }
  };

  // Handle User Selection
  const updateUserName = (event, data) => {
    if (data) {
      setSelectedUser(data);

      setUserId(data.user_id || data.id);
      setShowAutoCompleteAvathar(false);
    } else {
      setSelectedUser(null);
      setShowAutoCompleteAvathar(true);
    }
  };

  // Fetch detailed data properly mapped
  useEffect(() => {
    if (isDetailedView && initialData?.id) {
      const fetchDetails = async () => {
        setLoadingDetails(true);
        try {
          const res = await customGetAPI(
            `${domain}advance/request/${initialData.id}/`,
          );
          // Extract the nested 'data' object reliably
          const extractedData = res.responseDetails || res.data || res;
          setDetailedData(extractedData);
        } catch (err) {
          console.error("Failed to fetch advance details", err);
        } finally {
          setLoadingDetails(false);
        }
      };
      fetchDetails();
    }
  }, [isDetailedView, initialData?.id, domain]);

  // Pre-fill Action Data with the original request values securely mapping dates
  useEffect(() => {
    if (isAction && initialData) {
      const repaymentDate =
        initialData.repayment_date || initialData.repayment_start_date || "";
      const formattedDate = repaymentDate
        ? dayjs(repaymentDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : "";

      setActionData({
        approved_amount: initialData.amount || "",
        repayment_mode: initialData.repayment_mode || "Full",
        approved_installments:
          initialData.no_of_installments ||
          initialData.number_of_installments ||
          1,
        approved_repayment_date: formattedDate,
        approver_comments: "",
        rejection_reason: "",
      });
    }
  }, [isAction, initialData]);

  // Handle Double nested 'data' scenarios if API response dictates
  const displayData = detailedData?.data || detailedData || initialData || {};
  const timelineHistory = buildStatusHistory(displayData);

  const isInstallmentCreate =
    createData.repayment_mode?.startsWith("Installment");
  const monthlyAmountCreate =
    isInstallmentCreate &&
    createData.amount &&
    createData.no_of_installments > 0
      ? (
          parseFloat(createData.amount) /
          parseInt(createData.no_of_installments)
        ).toFixed(2)
      : 0;

  const isInstallmentAction =
    actionData.repayment_mode?.startsWith("Installment");

  const monthlyAmountAction =
    isInstallmentAction &&
    actionData.approved_amount &&
    actionData.approved_installments > 0
      ? (
          parseFloat(actionData.approved_amount) /
          parseInt(actionData.approved_installments)
        ).toFixed(2)
      : 0;

  // Handlers
  const handleCreateChange = (field, value) => {
    // Validate date fields - prevent past dates
    if (field === "repayment_date" && value) {
      const selectedDate = dayjs(value, "YYYY-MM-DD");
      const today = dayjs().startOf("day");
      if (selectedDate.isBefore(today)) {
        showToast("error", "Please select a future date for repayment.");
        return;
      }
    }

    setCreateData((prev) => {
      const newState = { ...prev, [field]: value };
      if (field === "repayment_mode" && value === "Full") {
        newState.no_of_installments = 1;
      }
      return newState;
    });
  };

  const handleActionChange = (field, value) => {
    // Validate date fields - prevent past dates
    if (field === "approved_repayment_date" && value) {
      const selectedDate = dayjs(value, "YYYY-MM-DD");
      const today = dayjs().startOf("day");
      if (selectedDate.isBefore(today)) {
        showToast("error", "Please select a future date for repayment.");
        return;
      }
    }

    setActionData((prev) => {
      const newState = { ...prev, [field]: value };
      if (field === "repayment_mode" && value === "Full") {
        newState.approved_installments = 1;
      }
      return newState;
    });
  };

  const handleCreateSubmit = async () => {
    setActionLoading(true);
    const createPayload = {
      user_id: userId,
      amount: parseFloat(createData.amount),
      reason: createData.reason,
      repayment_mode: createData.repayment_mode,
      repayment_date: createData.repayment_date,
      no_of_installments: isInstallmentCreate
        ? parseInt(createData.no_of_installments)
        : 1,
    };

    try {
      // Step 1: Create the advance request
      const createRes = await customAPI(
        `${domain}advance/request/`,
        "POST",
        createPayload,
      );
      if (!createRes.ok) {
        showToast("error", createRes.message || "Failed to create request.");
        setActionLoading(false);
        return;
      }

      // Extract the advance ID from response
      const advanceId =
        createRes?.data?.id ||
        createRes?.responseDetails?.data?.id ||
        createRes?.responseDetails?.id;
      if (!advanceId) {
        showToast("error", "Failed to retrieve advance ID for approval.");
        setActionLoading(false);
        return;
      }

      // Step 2: Approve the created advance
      const approvePayload = {
        approver_id: userDetails?.id,
        approved_amount: parseFloat(createData.amount),
        repayment_mode: createData.repayment_mode,
        approved_installments: isInstallmentCreate
          ? parseInt(createData.no_of_installments)
          : 1,
        approved_repayment_date: createData.repayment_date,
        approver_comments: "Auto-approved via Team Zone creation.",
      };

      const approveRes = await customAPI(
        `${domain}advance/request/${advanceId}/approve/`,
        "PUT",
        approvePayload,
      );

      if (approveRes.ok) {
        showToast(
          "success",
          "Advance request created and approved successfully.",
        );
        if (reCallApi) reCallApi(true);
        if (onClose) onClose();
      } else {
        showToast("error", approveRes.message || "Failed to approve request.");
      }
    } catch (error) {
      showToast("error", "Error processing request.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    const payload = {
      approver_id: userDetails?.id,
      approved_amount: parseFloat(actionData.approved_amount),
      repayment_mode: actionData.repayment_mode,
      approved_installments: isInstallmentAction
        ? parseInt(actionData.approved_installments)
        : 1,
      approved_repayment_date: actionData.approved_repayment_date,
      approver_comments: actionData.approver_comments,
    };

    try {
      const res = await customAPI(
        `${domain}advance/request/${initialData.id}/approve/`,
        "PUT",
        payload,
      );
      if (res.ok) {
        showToast("success", "Advance Request Approved Successfully");
        if (reCallApi) reCallApi(true);
        if (onClose) onClose();
      } else {
        showToast("error", res.message || "Failed to approve request.");
      }
    } catch (error) {
      showToast("error", "Error processing approval.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!actionData.rejection_reason) {
      showToast("warning", "Please provide a rejection reason.");
      return;
    }
    setActionLoading(true);
    const payload = {
      approver_id: userDetails?.id,
      approver_comments: actionData.approver_comments,
      rejection_reason: actionData.rejection_reason,
    };

    try {
      const res = await customAPI(
        `${domain}advance/request/${initialData.id}/reject/`,
        "PUT",
        payload,
      );
      if (res.ok) {
        showToast("success", "Advance Request Rejected");
        if (reCallApi) reCallApi(true);
        if (onClose) onClose();
      } else {
        showToast("error", res.message || "Failed to reject request.");
      }
    } catch (error) {
      showToast("error", "Error processing rejection.");
    } finally {
      setActionLoading(false);
    }
  };

  const renderDetailedView = () => (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Box
          sx={{
            border: "1px solid #00ab66",
            color: "#00ab66",
            borderRadius: 4,
            px: 2,
            py: 0.5,
            fontSize: "0.8rem",
            fontWeight: "bold",
          }}
        >
          {displayData.status === "Active" ? "Active" : "Partially Recovered"}
        </Box>
      </Box>

      <Stack direction="row" spacing={2} mb={3}>
        <SummaryCard
          title="Advance Amount"
          amount={displayData.approved_amount || displayData.amount || "-"}
          icon={ShoppingBagOutlinedIcon}
          color="#2ecc71"
          lightColor="#eafaf1"
        />
        <SummaryCard
          title="Paid Amount"
          amount={displayData.paid_amount || "0.00"}
          icon={AccountBalanceWalletOutlinedIcon}
          color="#e91e63"
          lightColor="#fce4ec"
        />
        <SummaryCard
          title="Balance"
          amount={displayData.balance_amount || "-"}
          icon={CardGiftcardOutlinedIcon}
          color="#3f51b5"
          lightColor="#e8eaf6"
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={2}>
          {[
            { label: "Request date", value: displayData.request_date },
            { label: "Amount", value: displayData.effective_amount },
            { label: "Reason", value: displayData.reason },
            {
              label: "Disbursement date",
              value: displayData.disbursement_date || "-",
            },
            { label: "Repayment Mode", value: displayData.repayment_mode },
            {
              label: "No of Instalement",
              value:
                displayData.approved_installments ||
                displayData.no_of_installments,
            },
          ].map((item, i) => (
            <React.Fragment key={i}>
              <Grid item xs={5}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {item.label}
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography variant="body2" color="text.secondary">
                  {item.value || "-"}
                </Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Paper>

      <Typography variant="subtitle1" fontWeight="bold" mb={2}>
        Repayment History
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Due Date</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Due</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Paid</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Balance</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Successfully mapped the dynamically parsed detailedData */}
            {displayData.repayment_schedule?.length > 0 ? (
              displayData.repayment_schedule.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.due_date || "-"}</TableCell>
                  <TableCell>{row.amount_due || "-"}</TableCell>
                  <TableCell>{row.amount_paid || "-"}</TableCell>
                  <TableCell>{row.balance || "-"}</TableCell>
                  <TableCell>{row.status || "-"}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  No schedule available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#fff",
        width: "100%",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
          pb: 1,
        }}
      >
        {/* Fixed Header Layout for Spacing and alignment */}
        <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2 }}>
          <IconButton
            onClick={onClose}
            sx={{ color: themes?.grayColor, mr: 1, ml: -1 }}
          >
            <DoubleArrow />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {isView
                ? isDetailedView
                  ? "Advance Details"
                  : "View Advance Request"
                : isAction
                  ? "Review Advance Request"
                  : "Issue Advance"}
            </Typography>
            <Typography {...infoStyle}>
              {isView
                ? "View the employee's payoff request."
                : isAction
                  ? "Approve or Reject the requested advance."
                  : "Create an auto-approved advance for a team member."}
            </Typography>
          </Box>
          {isView &&
            onAction &&
            !isDetailedView &&
            initialData?.status === "Pending" && (
              <IconButton
                size="small"
                onClick={onAction}
                sx={{ bgcolor: "#e8f5e9", color: "#00ab66" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
        </Box>
      </Paper>

      <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        {isCreate && (
          <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Department
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Select the department for the employee (or keep "All
                Departments" for any employee).
              </Typography>
              <CustomAutoComplete
                dataList={[
                  {
                    id: defaultAllSelection,
                    department_name: "All Department",
                    label: "All Department",
                  },
                  ...(departmentData?.map((obj) => ({
                    ...obj,
                    label: obj.department_name,
                  })) || []),
                ]}
                selectedValue={departmentName}
                updateDataFunction={updateDepartmentName}
                placeHolder="Select department name"
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Employee
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Select the employee to issue the advance to.
              </Typography>
              <CustomAutoCompleteWithLogo
                disabled={!membersData || membersData.length === 0}
                dataList={membersData
                  ?.filter(
                    (obj) =>
                      obj.user_name && obj.user_name.toLowerCase() !== "all",
                  )
                  ?.map((obj) => ({
                    ...obj,
                    label: obj?.user_name,
                    image: obj?.image_url,
                  }))}
                selectedValue={selectedUser}
                updateDataFunction={updateUserName}
                placeHolder={
                  !membersData || membersData.length === 0
                    ? "No employees available"
                    : "Select employee name"
                }
                showAvathar={showAutoCompleteAvathar}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
              />
            </Box>
          </Box>
        )}
        {!isCreate && (
          <Box sx={{ mb: 3 }}>
            <UserDetailsRow
              userDetails={{
                image_url:
                  displayData?.image_url ||
                  initialData?.image_url ||
                  userDetails?.image_url,
                employee_id:
                  displayData?.employee_id || initialData?.employee_id,
                user_name: displayData?.user_name || initialData?.user_name,
                department_name:
                  displayData?.department || initialData?.department,
                designation:
                  displayData?.designation || initialData?.designation,
              }}
            />
          </Box>
        )}

        {isView ? (
          loadingDetails ? (
            <LoaderWithoutHeight info="Loading details..." />
          ) : isDetailedView ? (
            renderDetailedView()
          ) : (
            <Box>
              <InfoCard
                label="Requested Amount"
                value={displayData.amount}
                icon={AccountBalanceWalletOutlinedIcon}
                color="#f0f7ff"
              />
              {displayData.repayment_mode === "Full" ? (
                <InfoCard
                  label="Repayment Date"
                  value={
                    displayData.repayment_date
                      ? dayjs(displayData.repayment_date, "DD-MM-YYYY").format(
                          "DD.MM.YYYY",
                        )
                      : "-"
                  }
                  icon={CalendarTodayIcon}
                  color="#d6e6e3"
                />
              ) : (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <InfoCard
                      label="No. of Installments"
                      value={displayData.no_of_installments}
                      icon={HelpOutlineIcon}
                      color="#fffcf0"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <InfoCard
                      label="Repayment Start Date"
                      value={
                        displayData.repayment_date
                          ? dayjs(
                              displayData.repayment_date,
                              "DD-MM-YYYY",
                            ).format("DD.MM.YYYY")
                          : "-"
                      }
                      icon={CalendarTodayIcon}
                      color="#e2f3f0"
                    />
                  </Box>
                </Box>
              )}
              <InfoCard
                label="Repayment Mode"
                value={displayData.repayment_mode}
                icon={ModeStandbyIcon}
                color="#fff0f6"
              />
              <InfoCard
                label="Reason"
                value={displayData.reason}
                icon={CommentIcon}
                color="#e9e6f7"
              />

              {timelineHistory.length > 0 && (
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    Status History
                  </Typography>
                  <CustomTimeline history={timelineHistory} />
                </Grid>
              )}
            </Box>
          )
        ) : isAction ? (
          /* ================= ACTION MODE (Approve/Reject) ================= */
          <Stack spacing={3}>
            {/* Read Only Context */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
              <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                Original Request
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {initialData.amount}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {initialData.request_date}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Reason
                  </Typography>
                  <Typography variant="body2">
                    {initialData.reason || "-"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ borderBottom: "1px solid #eee", pb: 1 }}
            >
              Approval Terms
            </Typography>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Approved Amount
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Enter the approved amount for this advance request.
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={actionData.approved_amount}
                onChange={(e) =>
                  handleActionChange("approved_amount", e.target.value)
                }
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Repayment mode
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Select the repayment mode for approval.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    onClick={() => handleActionChange("repayment_mode", "Full")}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      borderRadius: 2,
                      border:
                        actionData.repayment_mode === "Full"
                          ? "2px solid #00ab66"
                          : "1px solid #ddd",
                      bgcolor:
                        actionData.repayment_mode === "Full"
                          ? "#f0fff4"
                          : "transparent",
                    }}
                  >
                    <FormControlLabel
                      value="Full"
                      control={
                        <Radio
                          checked={actionData.repayment_mode === "Full"}
                          size="small"
                          sx={{ color: "#00ab66" }}
                        />
                      }
                      label={
                        <Typography variant="subtitle2" fontWeight="bold">
                          Full
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ ml: 3.5 }}
                    >
                      One-time deduction in the next applicable payroll.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    onClick={() =>
                      handleActionChange("repayment_mode", "Installment")
                    }
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      borderRadius: 2,
                      border:
                        actionData.repayment_mode === "Installment"
                          ? "2px solid #00ab66"
                          : "1px solid #ddd",
                      bgcolor:
                        actionData.repayment_mode === "Installment"
                          ? "#f0fff4"
                          : "transparent",
                    }}
                  >
                    <FormControlLabel
                      value="Installment"
                      control={
                        <Radio
                          checked={actionData.repayment_mode === "Installment"}
                          size="small"
                          sx={{ color: "#00ab66" }}
                        />
                      }
                      label={
                        <Typography variant="subtitle2" fontWeight="bold">
                          Installment
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ ml: 3.5 }}
                    >
                      Amount will be split into installments and deducted
                      monthly.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {isInstallmentAction && (
              <Box>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Approved Installments
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ minHeight: 40, mb: 1 }}
                    >
                      Number of payroll cycles over which the advance will be
                      repaid.
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={actionData.approved_installments}
                      onChange={(e) =>
                        handleActionChange(
                          "approved_installments",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Monthly Repayment Amount
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ minHeight: 40, mb: 1 }}
                    >
                      Amount to be deducted from each payroll cycle.
                    </Typography>
                    <Box
                      sx={{
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "#00ab66", fontWeight: "bold" }}
                      >
                        {monthlyAmountAction}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {isInstallmentAction
                  ? "Repayment Start Date"
                  : "Repayment date"}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={actionData.approved_repayment_date}
                onChange={(e) =>
                  handleActionChange("approved_repayment_date", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: dayjs().format("YYYY-MM-DD"),
                }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Approver Comments
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                placeholder="Optional notes on approval..."
                value={actionData.approver_comments}
                onChange={(e) =>
                  handleActionChange("approver_comments", e.target.value)
                }
              />
            </Box>

            <Box sx={{ borderTop: "1px dashed #ffcccb", pt: 2 }}>
              <Typography
                variant="subtitle2"
                color="error"
                fontWeight="bold"
                gutterBottom
              >
                Rejection Reason (Required for Rejection)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                placeholder="Enter reason if rejecting..."
                value={actionData.rejection_reason}
                onChange={(e) =>
                  handleActionChange("rejection_reason", e.target.value)
                }
              />
            </Box>
          </Stack>
        ) : (
          /* ================= CREATE MODE (Team Zone) ================= */
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Amount
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Enter the advance amount to be issued to the employee.
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={createData.amount}
                onChange={(e) => handleCreateChange("amount", e.target.value)}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Reason
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Mention the reason for issuing this advance.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={createData.reason}
                onChange={(e) => handleCreateChange("reason", e.target.value)}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Repayment mode
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Choose the repayment mode for the advance.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    onClick={() => handleCreateChange("repayment_mode", "Full")}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      borderRadius: 2,
                      border:
                        createData.repayment_mode === "Full"
                          ? "2px solid #00ab66"
                          : "1px solid #ddd",
                      bgcolor:
                        createData.repayment_mode === "Full"
                          ? "#f0fff4"
                          : "transparent",
                    }}
                  >
                    <FormControlLabel
                      value="Full"
                      control={
                        <Radio
                          checked={createData.repayment_mode === "Full"}
                          size="small"
                          sx={{ color: "#00ab66" }}
                        />
                      }
                      label={
                        <Typography variant="subtitle2" fontWeight="bold">
                          Full
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ ml: 3.5 }}
                    >
                      One-time deduction in the next applicable payroll.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    onClick={() =>
                      handleCreateChange("repayment_mode", "Installment")
                    }
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      borderRadius: 2,
                      border:
                        createData.repayment_mode === "Installment"
                          ? "2px solid #00ab66"
                          : "1px solid #ddd",
                      bgcolor:
                        createData.repayment_mode === "Installment"
                          ? "#f0fff4"
                          : "transparent",
                    }}
                  >
                    <FormControlLabel
                      value="Installment"
                      control={
                        <Radio
                          checked={createData.repayment_mode === "Installment"}
                          size="small"
                          sx={{ color: "#00ab66" }}
                        />
                      }
                      label={
                        <Typography variant="subtitle2" fontWeight="bold">
                          Installment
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ ml: 3.5 }}
                    >
                      Amount will be split into installments and deducted
                      monthly.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {isInstallmentCreate && (
              <Box>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      No. of Installments
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ minHeight: 40, mb: 1 }}
                    >
                      Number of payroll cycles over which the advance will be
                      repaid.
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={createData.no_of_installments}
                      onChange={(e) =>
                        handleCreateChange("no_of_installments", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Monthly Repayment Amount
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ minHeight: 40, mb: 1 }}
                    >
                      Amount to be deducted from each payroll cycle.
                    </Typography>
                    <Box
                      sx={{
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "#00ab66", fontWeight: "bold" }}
                      >
                        {monthlyAmountCreate}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
                {isInstallmentCreate
                  ? "Repayment Start Date"
                  : "Repayment date"}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={createData.repayment_date}
                onChange={(e) =>
                  handleCreateChange("repayment_date", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: dayjs().format("YYYY-MM-DD"),
                }}
              />
            </Box>
          </Stack>
        )}
      </Box>

      {/* FOOTER - Dynamic based on Mode */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid #eee",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        {isCreate && (
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={
              !userId ||
              !createData.amount ||
              !createData.reason ||
              actionLoading
            }
            sx={{ bgcolor: "#84deb8", px: 4 }}
          >
            {actionLoading ? "Processing..." : "Approve"}
          </Button>
        )}

        {isAction && (
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={handleReject}
              disabled={actionLoading || !actionData.rejection_reason}
              sx={{ px: 3, fontWeight: "bold" }}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              onClick={handleApprove}
              disabled={actionLoading || !actionData.approved_amount}
              sx={{ bgcolor: "#84deb8", px: 4, fontWeight: "bold" }}
            >
              Approve
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TeamAdvanceSalaryPanel;
